import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Download,
  Upload,
  FileText,
  RefreshCw,
  History,
  Undo2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';

export function TranslationSettings() {
  const { t, i18n } = useTranslation();
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importLogs, setImportLogs] = useState([]);
  const [showImportResult, setShowImportResult] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);

  // Fetch import logs
  const fetchImportLogs = useCallback(async () => {
    try {
      const response = await api.get('/translations/import-logs');
      setImportLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch import logs:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchImportLogs();
  }, [fetchImportLogs]);

  // Export translations
  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await api.get('/translations/export', {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `translations_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('settings.translations.export_success') || 'Translations exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export translations');
    } finally {
      setLoading(false);
    }
  };

  // Download sample CSV
  const handleDownloadSample = async () => {
    try {
      const response = await api.get('/translations/sample', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'translation_sample.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download sample file');
    }
  };

  // Import translations
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported');
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      event.target.value = '';
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/translations/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      setShowImportResult(true);
      
      // Refresh translations in i18n
      await refreshTranslations();
      
      // Refresh import logs
      fetchImportLogs();
      
      if (response.data.errors === 0) {
        toast.success(`${response.data.updated} translations updated successfully`);
      } else {
        toast.warning(`Import completed with ${response.data.errors} errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import translations');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // Refresh translations in i18n from API
  const refreshTranslations = async () => {
    try {
      const currentLang = i18n.language;
      const response = await api.get(`/translations/public?locale=${currentLang}`);
      
      // Update i18n resources
      i18n.addResourceBundle(currentLang, 'translation', response.data, true, true);
      
      toast.success('Translations refreshed');
    } catch (error) {
      console.error('Failed to refresh translations:', error);
    }
  };

  // Rollback import
  const handleRollback = async () => {
    if (!selectedLogId) return;

    setLoading(true);
    try {
      await api.post(`/translations/rollback/${selectedLogId}`);
      toast.success('Translation import rolled back successfully');
      
      // Refresh translations
      await refreshTranslations();
      
      // Refresh import logs
      fetchImportLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollback translations');
    } finally {
      setLoading(false);
      setShowRollbackConfirm(false);
      setSelectedLogId(null);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Export/Import Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('settings.translations.title') || 'Translation Management'}
          </CardTitle>
          <CardDescription>
            {t('settings.translations.description') || 'Export and import UI translations using CSV files'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Instructions */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('settings.translations.instructions') || 
                'Edit translations using Excel or Google Sheets. Do not change the "key" column. Empty values will use the default translation.'}
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4">
            {/* Export Button */}
            <Button 
              onClick={handleExport} 
              disabled={loading}
              className="gap-2"
              data-testid="export-translations-btn"
            >
              <Download className="h-4 w-4" />
              {loading ? 'Exporting...' : (t('settings.translations.export') || 'Export Translations (CSV)')}
            </Button>

            {/* Import Button */}
            <div className="relative">
              <Input
                type="file"
                accept=".csv"
                onChange={handleImport}
                disabled={importing}
                className="absolute inset-0 opacity-0 cursor-pointer"
                data-testid="import-translations-input"
              />
              <Button 
                variant="outline" 
                disabled={importing}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                {importing ? 'Importing...' : (t('settings.translations.import') || 'Import Translations')}
              </Button>
            </div>

            {/* Download Sample */}
            <Button 
              variant="ghost" 
              onClick={handleDownloadSample}
              className="gap-2"
            >
              <FileText className="h-4 w-4" />
              {t('settings.translations.download_sample') || 'Download Sample CSV'}
            </Button>

            {/* Refresh Translations */}
            <Button 
              variant="ghost" 
              onClick={refreshTranslations}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('settings.translations.refresh') || 'Refresh'}
            </Button>
          </div>

          {/* Supported Languages Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{t('settings.translations.supported_languages') || 'Supported languages'}:</span>
            <Badge variant="outline">English (en)</Badge>
            <Badge variant="outline">Indonesian (id)</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Import History Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {t('settings.translations.import_history') || 'Import History'}
          </CardTitle>
          <CardDescription>
            {t('settings.translations.import_history_desc') || 'View past imports and rollback if needed'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {importLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('settings.translations.no_imports') || 'No import history yet'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.date') || 'Date'}</TableHead>
                  <TableHead>{t('settings.translations.filename') || 'Filename'}</TableHead>
                  <TableHead>{t('settings.translations.imported_by') || 'Imported By'}</TableHead>
                  <TableHead className="text-center">{t('settings.translations.result') || 'Result'}</TableHead>
                  <TableHead className="text-center">{t('common.status') || 'Status'}</TableHead>
                  <TableHead className="text-right">{t('common.actions') || 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.filename}
                    </TableCell>
                    <TableCell>{log.imported_by_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-2 text-sm">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                          {log.updated_count}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <AlertCircle className="h-3 w-3" />
                          {log.skipped_count}
                        </span>
                        {log.error_count > 0 && (
                          <span className="flex items-center gap-1 text-red-500">
                            <XCircle className="h-3 w-3" />
                            {log.error_count}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {log.is_rolled_back ? (
                        <Badge variant="outline" className="text-orange-500 border-orange-500">
                          Rolled Back
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-500 border-green-500">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!log.is_rolled_back && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLogId(log.id);
                            setShowRollbackConfirm(true);
                          }}
                          className="gap-1 text-orange-500 hover:text-orange-600"
                        >
                          <Undo2 className="h-4 w-4" />
                          Rollback
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Import Result Dialog */}
      <Dialog open={showImportResult} onOpenChange={setShowImportResult}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.errors === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              {t('settings.translations.import_result') || 'Import Result'}
            </DialogTitle>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{importResult.updated}</div>
                  <div className="text-xs text-muted-foreground">Updated</div>
                </div>
                <div className="p-3 bg-gray-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">{importResult.skipped}</div>
                  <div className="text-xs text-muted-foreground">Skipped</div>
                </div>
                <div className="p-3 bg-red-500/10 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{importResult.errors}</div>
                  <div className="text-xs text-muted-foreground">Errors</div>
                </div>
              </div>

              {importResult.warnings?.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-1">Warnings:</div>
                    <ul className="text-sm list-disc list-inside">
                      {importResult.warnings.slice(0, 5).map((w, i) => (
                        <li key={i}>{w.message} (row {w.row})</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {importResult.error_details?.length > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-1">Errors:</div>
                    <ul className="text-sm list-disc list-inside">
                      {importResult.error_details.slice(0, 5).map((e, i) => (
                        <li key={i}>Row {e.row}: {e.message}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowImportResult(false)}>
              {t('common.close') || 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog open={showRollbackConfirm} onOpenChange={setShowRollbackConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              {t('settings.translations.confirm_rollback') || 'Confirm Rollback'}
            </DialogTitle>
            <DialogDescription>
              {t('settings.translations.rollback_warning') || 
                'This will restore translations to the state before this import. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRollbackConfirm(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRollback}
              disabled={loading}
            >
              {loading ? 'Rolling back...' : (t('settings.translations.rollback') || 'Rollback')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TranslationSettings;
